import { useTranslation } from '@jq4race/i18n';
import type { EventSummaryResponse } from '@jq4race/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@jq4race/ui';
import { CalendarDays } from 'lucide-react';

interface FlashEventPickerProps {
  events: EventSummaryResponse[];
  loading: boolean;
  error: string | null;
  onSelect: (eventId: string) => void;
}

/** Operator-facing picker for the currently `active` events (S4-UI, R4.1). */
export function FlashEventPicker({ events, loading, error, onSelect }: FlashEventPickerProps) {
  const { t } = useTranslation('flash');

  if (loading) {
    return <p className="jq-body-sm text-muted-foreground">{t('eventPicker.loading')}</p>;
  }

  if (error) {
    return (
      <div
        role="alert"
        className="rounded-sm border border-destructive/60 bg-destructive/10 px-3 py-3 text-[13px] text-foreground"
      >
        {error}
      </div>
    );
  }

  if (events.length === 0) {
    return <p className="jq-body-sm text-muted-foreground">{t('eventPicker.empty')}</p>;
  }

  return (
    <ul className="space-y-3">
      {events.map((event) => (
        <li key={event.id}>
          <Card>
            <CardHeader className="flex-row items-center justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="truncate">{event.name}</CardTitle>
                <p className="mt-1 flex items-center gap-1.5 text-[13px] text-muted-foreground">
                  <CalendarDays className="size-3.5 shrink-0" strokeWidth={1.5} />
                  {event.scheduledDate}
                  {event.scheduledTime ? ` · ${event.scheduledTime}` : ''}
                </p>
              </div>
              <Badge variant="success">{t('eventPicker.statusActive')}</Badge>
            </CardHeader>
            <CardContent>
              <Button size="touch" className="w-full" onClick={() => onSelect(event.id)}>
                {t('eventPicker.startFlashMode')}
              </Button>
            </CardContent>
          </Card>
        </li>
      ))}
    </ul>
  );
}
